/**
 * Real execution traces captured from PicoAgents library.
 * Trace JSON files are stored in /public/traces/ and loaded dynamically.
 *
 * Event types from picoagents.types:
 * - TaskStartEvent, TaskCompleteEvent
 * - ModelCallEvent, ModelResponseEvent
 * - ToolCallEvent, ToolCallResponseEvent
 * - UserMessage, AssistantMessage, ToolMessage
 * - AgentResponse
 *
 * Workflow events from picoagents.workflow.core._models:
 * - WorkflowStartedEvent, WorkflowCompletedEvent
 * - StepStartedEvent, StepCompletedEvent
 * - EdgeActivatedEvent
 */

// Raw event interface - matches Pydantic model_dump() output
export interface RawEvent {
  _elapsed_ms: number
  _class: string
  timestamp?: string
  source?: string
  event_type?: string
  content?: string
  role?: string
  // Agent events
  task?: string
  model?: string
  response?: string
  has_tool_calls?: boolean
  tool_name?: string
  parameters?: Record<string, unknown>
  call_id?: string
  result?:
    | {
        success: boolean
        result: string
        error?: string | null
        metadata?: Record<string, unknown>
      }
    | string
  // Workflow events
  workflow_id?: string
  step_id?: string
  input_data?: Record<string, unknown>
  output_data?: Record<string, unknown>
  duration_seconds?: number
  from_step?: string
  to_step?: string
  data?: Record<string, unknown>
  initial_input?: Record<string, unknown>
  execution?: Record<string, unknown>
  // Message types
  usage?: {
    duration_ms: number
    llm_calls: number
    tokens_input: number
    tokens_output: number
    tool_calls: number
    memory_operations: number
    cost_estimate: number | null
  }
  finish_reason?: string
  context?: Record<string, unknown>
  // Streaming events
  chunk?: string
  // Orchestration events
  selected_agent?: string
  stop_reason?: string
  executing_agent?: string
  final_result?: string
}

export interface ExecutionTrace {
  id: string
  name: string
  filename: string
  chapter: string
  description: string
  github_url: string
  code: string
  task?: string
  input?: Record<string, unknown>
  events: RawEvent[]
  captured_at: string
}

// Trace file mapping
const TRACE_FILES: Record<string, string> = {
  basic_agent: '/traces/basic_agent.json',
  workflows: '/traces/workflow.json',
  // These will be added as we capture more traces
  multi_agent: '/traces/multi_agent.json',
  streaming: '/traces/streaming.json',
}

// Cache for loaded traces
const traceCache: Record<string, ExecutionTrace> = {}

/**
 * Load a trace from the public folder
 */
export async function loadTrace(id: string): Promise<ExecutionTrace | null> {
  // Return from cache if available
  if (traceCache[id]) {
    return traceCache[id]
  }

  const path = TRACE_FILES[id]
  if (!path) {
    console.warn(`No trace file mapped for id: ${id}`)
    return null
  }

  try {
    const response = await fetch(path)
    if (!response.ok) {
      console.warn(`Failed to load trace ${id}: ${response.status}`)
      return null
    }
    const trace = (await response.json()) as ExecutionTrace
    traceCache[id] = trace
    return trace
  } catch (error) {
    console.warn(`Error loading trace ${id}:`, error)
    return null
  }
}

/**
 * Preload all traces for faster playback
 */
export async function preloadAllTraces(): Promise<void> {
  await Promise.all(Object.keys(TRACE_FILES).map((id) => loadTrace(id)))
}

/**
 * Get all available trace IDs
 */
export function getAvailableTraceIds(): string[] {
  return Object.keys(TRACE_FILES)
}

// For backward compatibility - provide sync access to cached traces
// Components should call preloadAllTraces() on mount
export const demoExecutions: Record<string, ExecutionTrace> = traceCache
